import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { useSubscription } from '@apollo/client';
import { useBackdropContext } from '../../../../../../context/backdrop';
import {
  GenericInputBistableModeInternal,
  GenericInputMonostableModeInternal,
  GenericInputTypeInternal,
  OnGenericInputStateChangeSubscription,
  OnGenericInputStateChangeSubscriptionVariables,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_GENERIC_INPUT_STATE_CHANGE } from '../../../../../../data-access/subscriptions/lavva-devices';
import { useTimeout } from '../../../../../../hooks/timeout/use-timeout';
import {
  GenericInputBistableModeInternalRequest,
  GenericInputMonostableModeInternalRequest,
  GenericInputTypeInternalRequest,
} from '../../../../types/graphql-rest-converter';
import { toastSuccess } from '../../../../../../utils/toast';
import { useSetInputType } from '../../../../api/generic-input/generic-input.hooks';
import { useDevicesAndChannelsContext } from '../../../../context/devices-and-channels';
import { ChannelType } from '../../../../types';
import { ChannelGenericInputType } from '../../../../types/channel/generic-input';

export const useUpdateInputType = (inputChannel: ChannelType) => {
  const { t } = useTranslation('channel-settings');
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { setTimeoutError, clearTimeoutError } = useTimeout();
  const { setChannelList } = useDevicesAndChannelsContext();
  const inputTypeGeneric = useSetInputType();
  const { data } = useSubscription<
    OnGenericInputStateChangeSubscription,
    OnGenericInputStateChangeSubscriptionVariables
  >(ON_GENERIC_INPUT_STATE_CHANGE, {
    variables: { channelId: inputChannel.id },
    skip: !inputChannel.id,
  });

  useEffect(() => {
    if (data?.onGenericInputStateChange && inputChannel) {
      turnOffBackdrop();

      setChannelList((prev) => {
        const tempList = cloneDeep(prev);
        const index = tempList.findIndex((x) => x.id === inputChannel.id);

        if (index !== -1) {
          (tempList[index].data as ChannelGenericInputType).genericInputType =
            data.onGenericInputStateChange.genericInputType;
          (tempList[index].data as ChannelGenericInputType).monostableMode =
            data.onGenericInputStateChange.monostableMode || undefined;
          (tempList[index].data as ChannelGenericInputType).bistableMode =
            data.onGenericInputStateChange.bistableMode || undefined;
        }

        clearTimeoutError();
        toastSuccess({ content: `${t('toast.editInputTypeSuccess', { alias: inputChannel.alias })}` });
        return tempList;
      });
    }
  }, [data]);

  const editInputType = (
    inputType: GenericInputTypeInternal,
    monostableMode: GenericInputMonostableModeInternal,
    bistableMode: GenericInputBistableModeInternal,
  ) => {
    if (inputChannel) {
      turnOnBackdrop();
      inputTypeGeneric.mutate(
        {
          deviceId: inputChannel.deviceId,
          channelId: inputChannel.id,
          genericInputType: GenericInputTypeInternalRequest[inputType],
          ...(inputType === GenericInputTypeInternal.Monostable
            ? { monostableMode: GenericInputMonostableModeInternalRequest[monostableMode] }
            : {}),
          ...(inputType === GenericInputTypeInternal.Bistable
            ? { bistableMode: GenericInputBistableModeInternalRequest[bistableMode] }
            : {}),
        },
        {
          onSuccess: () => {
            turnOnBackdrop();
            setTimeoutError();
          },
          onError: () => {
            turnOffBackdrop();
          },
        },
      );
    }
  };

  return {
    editInputType,
  };
};
